// frontend/src/components/CuttingPlan/OrderSelection.jsx
import React, { useState, useEffect } from 'react';
import { cuttingPlansAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiCheckSquare, FiSquare, FiSearch, FiTrash2 } from 'react-icons/fi';

// ── Pivot rows: group by (production_order + line_ref), spread sizes into cols ─
const pivotRows = (rows) => {
  const grouped = {};
  const positions = new Set();

  rows.forEach(row => {
    const key = `${row.production_order}||${row.line_ref}`;
    if (!grouped[key]) {
      grouped[key] = {
        id:               row.id,
        production_order: row.production_order,
        line_ref:         row.line_ref,
        product:          row.product,
        color:            row.color,
        is_selected:      row.is_selected,
        qty:              {},
        row_ids:          [],
      };
    }
    if (row.size_position) {
      grouped[key].qty[row.size_position] =
        (grouped[key].qty[row.size_position] || 0) + parseFloat(row.pending_quantity || 0);
      positions.add(row.size_position);
    }
    grouped[key].row_ids.push(row.id);
  });

  const sortedPositions = [...positions].sort((a, b) => a - b);
  return { pivoted: Object.values(grouped), positions: sortedPositions };
};

const calcTotals = (pivoted, positions) => {
  const totals = {};
  let grand = 0;
  positions.forEach(pos => {
    totals[pos] = pivoted
      .filter(r => r.is_selected)
      .reduce((sum, r) => sum + (r.qty[pos] || 0), 0);
    grand += totals[pos];
  });
  return { totals, grand };
};

// ── Main ──────────────────────────────────────────────────────────────────────
const OrderSelection = ({ importedOrders = null, onOrdersSelected }) => {
  const { t } = useTranslation();

  // Editable textarea: one production_order per line
  const [orderText,   setOrderText]   = useState('');

  // Step 2: detail pivot
  const [pivoted,     setPivoted]     = useState([]);
  const [positions,   setPositions]   = useState([]);
  const [color,       setColor]       = useState('');
  const [loadingDet,  setLoadingDet]  = useState(false);
  const [toggling,    setToggling]    = useState(null);

  // When parent pushes imported orders, populate textarea
  useEffect(() => {
    if (importedOrders !== null) {
      const lines = importedOrders.map(o => o.production_order).join('\n');
      setOrderText(lines);
      setPivoted([]);
      setPositions([]);
      notifyParent([]);
    }
  }, [importedOrders]);

  const notifyParent = (rows) => {
    if (onOrdersSelected) {
      onOrdersSelected(rows.filter(r => r.is_selected).map(r => r.production_order));
    }
  };

  // Parse textarea into array of trimmed non-empty order numbers
  const getParsedOrders = () =>
    orderText.split('\n').map(l => l.trim()).filter(Boolean);

  // Load step-2 detail using textarea orders + color
  const handleLoadDetails = async () => {
    const orders = getParsedOrders();
    if (!orders.length) return;
    setLoadingDet(true);
    try {
      const res = await cuttingPlansAPI.loadOrderDetails({
        productionOrders: orders,
        color: color.trim() || null,
      });
      if (res.data.success) {
        const rows = res.data.data || [];
        const { pivoted: piv, positions: pos } = pivotRows(rows);
        setPivoted(piv);
        setPositions(pos);
        notifyParent(piv);
      }
    } catch (err) {
      console.error('loadOrderDetails error:', err);
    } finally {
      setLoadingDet(false);
    }
  };

  // Toggle a pivot row
  const handleToggleRow = async (row) => {
    if (toggling) return;
    const newVal = !row.is_selected;
    setToggling(row.production_order + row.line_ref);
    const upd = pivoted.map(r =>
      r.production_order === row.production_order && r.line_ref === row.line_ref
        ? { ...r, is_selected: newVal } : r
    );
    setPivoted(upd);
    notifyParent(upd);
    try {
      await Promise.all(row.row_ids.map(id => cuttingPlansAPI.toggleSelection(id, newVal)));
    } catch {}
    setToggling(null);
  };

  const handleSelectAllRows = () => {
    const upd = pivoted.map(r => ({ ...r, is_selected: true }));
    setPivoted(upd);
    notifyParent(upd);
    upd.flatMap(r => r.row_ids).forEach(id => cuttingPlansAPI.toggleSelection(id, true).catch(() => {}));
  };

  const handleClearAllRows = () => {
    const upd = pivoted.map(r => ({ ...r, is_selected: false }));
    setPivoted(upd);
    notifyParent(upd);
    upd.flatMap(r => r.row_ids).forEach(id => cuttingPlansAPI.toggleSelection(id, false).catch(() => {}));
  };

  const parsedOrders    = getParsedOrders();
  const selectedRowCount = pivoted.filter(r => r.is_selected).length;
  const { totals, grand } = calcTotals(pivoted, positions);

  return (
    <div>

      {/* ── Editable order list ───────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>
            {t('cutting_plans.production_orders', 'Ordens de Fabrico')}
            {parsedOrders.length > 0 && (
              <span style={{
                marginLeft: 8, fontSize: 11, fontWeight: 600,
                padding: '1px 8px', borderRadius: 10,
                background: 'var(--gray-100)', color: 'var(--gray-500)',
              }}>
                {parsedOrders.length}
              </span>
            )}
          </label>
          {parsedOrders.length > 0 && (
            <button type="button"
              onClick={() => { setOrderText(''); setPivoted([]); setPositions([]); notifyParent([]); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--gray-400)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3,
              }}>
              <FiTrash2 size={12}/> {t('cutting_plans.clear', 'Limpar')}
            </button>
          )}
        </div>
        <textarea
          value={orderText}
          onChange={e => setOrderText(e.target.value)}
          placeholder={t('cutting_plans.orders_placeholder',
            'Uma ordem por linha\nex:\nCP22600040\nCP22600041\nCP22600042')}
          rows={Math.max(4, Math.min(parsedOrders.length + 1, 12))}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 13,
            fontFamily: 'monospace', lineHeight: 1.6,
            border: '1px solid var(--gray-200)', borderRadius: 8,
            resize: 'vertical', boxSizing: 'border-box',
            background: parsedOrders.length ? '#f9fffe' : 'white',
          }}
        />
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 3 }}>
          {t('cutting_plans.orders_hint', 'Pode editar, adicionar ou remover ordens manualmente.')}
        </div>
      </div>

      {/* ── Color + Load button ───────────────────────────────────────────── */}
      {parsedOrders.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)' }}>
              {t('cutting_plans.color_filter', 'Cor (opcional)')}
            </label>
            <input
              type="text"
              value={color}
              onChange={e => setColor(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLoadDetails()}
              placeholder="ex: GN101"
              maxLength={20}
              style={{
                width: 120, height: 32, padding: '0 8px',
                fontSize: 13, border: '1px solid var(--gray-300)', borderRadius: 6,
              }}
            />
          </div>
          <button type="button" className="btn-primary btn-sm"
            onClick={handleLoadDetails}
            disabled={loadingDet || !parsedOrders.length}
            style={{ height: 32, whiteSpace: 'nowrap' }}>
            <FiSearch size={13}/>
            {loadingDet
              ? t('cutting_plans.loading', 'A carregar…')
              : t('cutting_plans.load_orders', 'Carregar Ordens')}
          </button>
        </div>
      )}

      {/* ── Pivot table ───────────────────────────────────────────────────── */}
      {pivoted.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
              {t('cutting_plans.orders', 'Ordens')}
            </h4>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 10,
              background: selectedRowCount ? '#d1fae5' : 'var(--gray-100)',
              color: selectedRowCount ? '#065f46' : 'var(--gray-500)', fontWeight: 600,
            }}>
              {selectedRowCount}/{pivoted.length}
            </span>
            <button type="button" className="btn-secondary btn-sm"
              onClick={handleSelectAllRows} style={{ fontSize: 11, marginLeft: 'auto' }}>
              {t('cutting_plans.select_all', 'Todas')}
            </button>
            <button type="button" className="btn-secondary btn-sm"
              onClick={handleClearAllRows} style={{ fontSize: 11 }}>
              {t('cutting_plans.clear_all', 'Nenhuma')}
            </button>
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                  <th style={thStyle}>Sel</th>
                  <th style={thStyle}>CP</th>
                  <th style={thStyle}>Linh</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Produto</th>
                  {positions.map(pos => (
                    <th key={pos} style={{ ...thStyle, minWidth: 44 }}>{pos}</th>
                  ))}
                  <th style={thStyle}>Cor</th>
                </tr>
              </thead>
              <tbody>
                {pivoted.map((row, i) => (
                  <tr key={`${row.production_order}-${row.line_ref}`}
                    onClick={() => handleToggleRow(row)}
                    style={{
                      cursor: 'pointer', userSelect: 'none',
                      background: row.is_selected ? '#f0fdf9' : i % 2 === 0 ? 'white' : '#fafafa',
                      borderBottom: '1px solid var(--gray-100)',
                      opacity: toggling === row.production_order + row.line_ref ? 0.6 : 1,
                    }}>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{ color: row.is_selected ? 'var(--primary)' : 'var(--gray-300)' }}>
                        {row.is_selected ? <FiCheckSquare size={14}/> : <FiSquare size={14}/>}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 500 }}>
                      {row.production_order}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{row.line_ref}</td>
                    <td style={{
                      ...tdStyle, textAlign: 'left', maxWidth: 200,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.product}
                    </td>
                    {positions.map(pos => (
                      <td key={pos} style={{
                        ...tdStyle,
                        color: row.qty[pos] ? 'var(--gray-900)' : 'var(--gray-300)',
                      }}>
                        {row.qty[pos] ? Math.round(row.qty[pos]) : ''}
                      </td>
                    ))}
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{row.color}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f0fdf9', borderTop: '2px solid var(--primary)', fontWeight: 700 }}>
                  <td colSpan={4} style={{ ...tdStyle, textAlign: 'right', color: 'var(--gray-600)', paddingRight: 12 }}>
                    Total
                  </td>
                  {positions.map(pos => (
                    <td key={pos} style={{ ...tdStyle, color: totals[pos] ? 'var(--primary)' : 'var(--gray-300)' }}>
                      {totals[pos] ? Math.round(totals[pos]) : ''}
                    </td>
                  ))}
                  <td style={tdStyle}/>
                </tr>
                <tr style={{ background: '#e6f7f5' }}>
                  <td colSpan={4} style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, fontSize: 11, color: 'var(--gray-500)' }}>
                    {t('cutting_plans.grand_total', 'Total geral')}
                  </td>
                  <td colSpan={positions.length + 1} style={{ ...tdStyle, fontWeight: 700, color: 'var(--primary)', textAlign: 'left' }}>
                    {Math.round(grand)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!parsedOrders.length && (
        <div style={{ color: 'var(--gray-400)', fontSize: 13, padding: '4px 0' }}>
          {t('cutting_plans.no_orders_hint',
            'Introduza um grupo de planeamento e clique em "Importar ERP", ou escreva as ordens directamente acima.')}
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '7px 10px', textAlign: 'center',
  fontSize: 11, fontWeight: 700, color: 'var(--gray-600)',
  whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '6px 10px', textAlign: 'center', fontSize: 12,
};

export default OrderSelection;